import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { StagePill } from "@/components/StagePill";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";
import {
  Users, Building2, User, Trophy, XCircle, Bell, Wifi, ShieldAlert,
  TrendingUp, Calendar, ListTodo, Briefcase, ArrowUpRight, Clock, Activity, AlertTriangle,
} from "lucide-react";
import { STAGE_STYLES } from "@/lib/constants";

const ROLE_KPIS = {
  master_admin: [
    { key: "total_leads", label: "Total Leads", icon: Users, link: "/leads" },
    { key: "company_leads", label: "Company", icon: Building2, link: "/leads?type=Company" },
    { key: "individual_leads", label: "Individual", icon: User, link: "/leads?type=Individual" },
    { key: "won", label: "Won", icon: Trophy, link: "/leads?stage=Won", accent: "emerald" },
    { key: "lost", label: "Lost", icon: XCircle, link: "/leads?stage=Lost", accent: "red" },
    { key: "pending_approvals", label: "Pending Approvals", icon: Bell, link: "/analytics", accent: "amber" },
    { key: "online_users", label: "Online Users", icon: Wifi, link: "/analytics", accent: "emerald" },
    { key: "failed_logins", label: "Failed Logins", icon: ShieldAlert, link: "/analytics", accent: "red" },
  ],
  admin: [
    { key: "team_leads", label: "Team Leads", icon: Users, link: "/leads" },
    { key: "assignments", label: "Assignments", icon: Briefcase, link: "/leads" },
    { key: "follow_ups", label: "Follow-ups Today", icon: Calendar, link: "/leads" },
  ],
  lead_generator: [
    { key: "leads_added", label: "Leads Added", icon: TrendingUp, link: "/leads" },
    { key: "assigned_leads", label: "Assigned Leads", icon: Briefcase, link: "/leads" },
  ],
  sales_executive: [
    { key: "assigned_leads", label: "Assigned Leads", icon: Briefcase, link: "/leads" },
    { key: "follow_ups", label: "Follow-ups Today", icon: Calendar, link: "/leads" },
    { key: "meetings", label: "Meetings", icon: ListTodo, link: "/leads?stage=Meeting+Scheduled" },
  ],
};

const ACCENTS = {
  emerald: { text: "text-emerald-600 dark:text-emerald-400", bar: "#10b981" },
  red: { text: "text-red-600 dark:text-red-400", bar: "#ef4444" },
  amber: { text: "text-amber-600 dark:text-amber-400", bar: "#f59e0b" },
  blue: { text: "text-blue-600 dark:text-blue-400", bar: "#3b82f6" },
  default: { text: "text-foreground", bar: "var(--theme-color)" },
};

const FUNNEL_ORDER = ["New Lead", "Contacted", "Meeting Scheduled", "Qualified", "Proposal Sent", "Negotiation", "Won"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aging, setAging] = useState({ items: [], counts: {} });
  const [cmd, setCmd] = useState(null);
  const [productivity, setProductivity] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
  api.get("/dashboard")
    .then(({ data }) => setData(data))
    .catch(() => setData({}));

  api.get("/leads-aging")
    .then(({ data }) => setAging(data))
    .catch(() => {});

  if (user?.role === "master_admin") {
    api.get("/analytics/command-center")
      .then(({ data }) => setCmd(data))
      .catch(() => {});

    api.get("/analytics/productivity")
      .then(({ data }) => setProductivity(data.slice(0, 5)))
      .catch(() => {});
  }

  const timer = setInterval(() => {
    setCurrentTime(new Date());
  }, 1000);

  return () => clearInterval(timer);
}, [user]);

  if (!user || !data) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const kpis = ROLE_KPIS[user.role] || [];
  const isMaster = user.role === "master_admin";

  // Funnel data
  const funnel = FUNNEL_ORDER.map((s) => ({
    stage: s.length > 14 ? s.slice(0, 12) + "…" : s,
    fullStage: s,
    count: data.by_stage?.[s] || 0,
  }));
  const totalForPct = funnel[0]?.count || 1;

  // Pie data
  const pipeData = Object.entries(data.by_stage || {})
    .filter(([, v]) => v > 0)
    .map(([stage, value]) => ({ name: stage, value }));

  // Today's follow-ups
  const today = new Date().toISOString().slice(0, 10);
  const followupsDue = (aging.items || []).filter((l) => {
    return l.next_follow_up && l.next_follow_up.slice(0, 10) <= today;
  }).slice(0, 6);

  // Stale leads
  const staleLeads = (aging.items || []).filter((l) => l.stale_bucket !== "fresh").slice(0, 6);

  return (
    <div className="p-6 sm:p-8 space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
  <div>
    <p className="text-label">Overview</p>

    <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">
      Good to see you, {user.full_name?.split(" ")[0]}
    </h1>

    <p className="text-sm text-muted-foreground mt-1">
      A live snapshot of your CRM today.
    </p>
  </div>

  <div className="border border-border rounded-sm px-4 py-3 bg-background text-right">
    <div className="text-xs text-muted-foreground">
      Current Date & Time (IST)
    </div>

    <div className="font-semibold">
      {currentTime.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })}
    </div>
  </div>
</header>

      {/* KPI grid */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          const value = data[k.key] ?? 0;
          const accent = ACCENTS[k.accent] || ACCENTS.default;
          return (
            <button
              key={k.key}
              onClick={() => navigate(k.link)}
              data-testid={`kpi-${k.key}`}
              className="text-left surface border border-border rounded-sm p-4 hover:border-foreground/30 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-label">{k.label}</span>
                <Icon className={`w-4 h-4 ${accent.text}`} />
              </div>
              <div className="flex items-end justify-between">
                <div className={`text-2xl font-semibold tracking-tight ${accent.text}`}>{value}</div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          );
        })}
      </section>

      {/* Funnel + Pipeline pie */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Lead Funnel" className="lg:col-span-2">
          {funnel.every((f) => f.count === 0) ? <Empty label="No leads yet." /> : (
            <div className="space-y-2">
              {funnel.map((f) => {
                const pct = totalForPct ? (f.count / totalForPct) * 100 : 0;
                const stageStyle = STAGE_STYLES[f.fullStage] || {};
                return (
                  <div key={f.fullStage} className="flex items-center gap-3" data-testid={`funnel-${f.fullStage.replace(/ /g, "-")}`}>
                    <div className="w-32 text-xs">{f.fullStage}</div>
                    <div className="flex-1 h-7 rounded-sm relative overflow-hidden bg-secondary">
                      <div
                        className={`h-full rounded-sm flex items-center px-2 text-xs font-medium ${stageStyle.light} ${stageStyle.dark}`}
                        style={{ width: `${Math.max(pct, f.count > 0 ? 8 : 0)}%`, minWidth: f.count > 0 ? "32px" : "0" }}
                      >
                        {f.count > 0 && f.count}
                      </div>
                    </div>
                    <div className="w-10 text-xs text-muted-foreground text-right mono">{pct.toFixed(0)}%</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title="Pipeline distribution">
          {pipeData.length === 0 ? <Empty label="No pipeline data." /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pipeData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {pipeData.map((entry, idx) => (
                    <Cell key={idx} fill={STAGE_STYLES[entry.name]?.light?.match(/bg-(\w+)-(\d+)/)
                      ? `var(--theme-color)` : "#888"} fillOpacity={0.8 - idx * 0.07} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </section>

      {/* Follow-ups + Stale */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Follow-ups Due" icon={Calendar} testId="widget-followups">
          {followupsDue.length === 0 ? <Empty label="Nothing due today." /> : (
            <div className="divide-y divide-border">
              {followupsDue.map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/leads?focus=${l.id}`)}
                  data-testid={`followup-${l.id}`}
                  className="w-full text-left py-2.5 flex items-center justify-between surface-hover rounded-sm px-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground truncate mono">{l.lead_no} · {l.company || "—"}</div>
                  </div>
                  <StagePill stage={l.stage} />
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card title="Stale Leads" icon={Clock} testId="widget-stale">
          {staleLeads.length === 0 ? <Empty label="All leads are fresh." /> : (
            <div className="divide-y divide-border">
              {staleLeads.map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/leads?focus=${l.id}`)}
                  data-testid={`stale-${l.id}`}
                  className="w-full text-left py-2.5 flex items-center justify-between surface-hover rounded-sm px-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground mono">
                      Idle {l.days_idle}d · Age {l.age_days}d
                    </div>
                  </div>
                  <StaleBadge bucket={l.stale_bucket} />
                </button>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* Recent activities + team performance (master) */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Recent leads" className="lg:col-span-2">
          {(data.recent_leads || []).length === 0 ? <Empty label="No recent leads." /> : (
            <div className="divide-y divide-border">
              {data.recent_leads.map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/leads?focus=${l.id}`)}
                  data-testid={`recent-lead-${l.id}`}
                  className="w-full py-2.5 flex items-center justify-between gap-4 surface-hover rounded-sm px-2 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground truncate mono">{l.lead_no} · {l.company || "Individual"}</div>
                  </div>
                  <StagePill stage={l.stage} />
                </button>
              ))}
            </div>
          )}
        </Card>

        {isMaster ? (
          <Card title="Team Performance" icon={Trophy} testId="widget-team">
            {productivity.length === 0 ? <Empty label="No data." /> : (
              <div className="divide-y divide-border">
                {productivity.slice(0, 5).map((p) => (
                  <div key={p.user_id} className="py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-sm flex items-center justify-center text-[11px] font-semibold text-white shrink-0"
                        style={{ backgroundColor: "var(--theme-color)" }}>
                        {p.full_name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </div>
                      <div className="text-sm truncate font-medium">{p.full_name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 whitespace-nowrap">
                      <span>{p.leads_created} created</span>
                      <span className="text-emerald-600 dark:text-emerald-400 mono">{p.won} won</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : (
          <Card title="Pending tasks" icon={ListTodo} testId="widget-tasks">
            <div className="space-y-2 text-sm">
              <Task label="Follow-ups today" count={data.follow_ups || followupsDue.length} />
              <Task label="Meetings scheduled" count={data.meetings || 0} />
              <Task label="Stale leads" count={(aging.counts?.stale_7 || 0) + (aging.counts?.stale_15 || 0) + (aging.counts?.stale_30 || 0)} accent="amber" />
            </div>
          </Card>
        )}
      </section>
    </div>
  );
}

function Card({ title, children, className = "", icon: Icon, testId }) {
  return (
    <div className={`border border-border rounded-sm bg-background ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Empty({ label }) { return <div className="text-sm text-muted-foreground text-center py-6">{label}</div>; }

function StaleBadge({ bucket }) {
  const map = {
    stale_7: { label: "7d+", cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200" },
    stale_15: { label: "15d+", cls: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200" },
    stale_30: { label: "30d+", cls: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200" },
  };
  const s = map[bucket]; if (!s) return null;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${s.cls}`}>{s.label}</span>;
}

function Task({ label, count, accent = "default" }) {
  const c = ACCENTS[accent] || ACCENTS.default;
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={`mono font-semibold ${c.text}`}>{count}</span>
    </div>
  );
}
